import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface PortalDropdownPosition {
  top: number;
  left: number;
  width: number;
}

export interface PortalDropdown<T extends HTMLElement> {
  isOpen: boolean;
  position: PortalDropdownPosition;
  triggerRef: RefObject<T | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  open: () => void;
  toggle: () => void;
  close: () => void;
}

const PANEL_OFFSET = 4;
const INITIAL_POSITION: PortalDropdownPosition = { top: 0, left: 0, width: 0 };

// Open/close state and fixed positioning for a dropdown panel that is
// portal'd to document.body (so ancestor overflow doesn't clip it). The
// position is read from the trigger when the panel opens: directly below it,
// left-aligned, with the trigger's width for panels that want to match it.
export function usePortalDropdown<T extends HTMLElement>(): PortalDropdown<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<PortalDropdownPosition>(INITIAL_POSITION);
  const triggerRef = useRef<T>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const readPosition = useCallback((): void => {
    const trigger = triggerRef.current;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      setPosition({ top: rect.bottom + PANEL_OFFSET, left: rect.left, width: rect.width });
    }
  }, []);

  const open = useCallback((): void => {
    readPosition();
    setIsOpen(true);
  }, [readPosition]);

  const toggle = useCallback((): void => {
    readPosition();
    setIsOpen((prev) => !prev);
  }, [readPosition]);

  const close = useCallback((): void => setIsOpen(false), []);

  // The panel is portal'd to document.body, so a single-ref click-outside
  // (e.g. qampo's useClickOutside) treats it as "outside" and closes on every
  // option click. Both the trigger and the panel must be checked here.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent): void => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        panelRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return { isOpen, position, triggerRef, panelRef, open, toggle, close };
}
